import fetch from './adminFetch'
import { PROFILE_CACHE_ID } from './user'

import type { User, SubGroup, UserPayload, AddUserPayload } from 'types'

export interface FullResp {
  users: User[]
  subGroups: SubGroup[]
}

export interface AddUsersResp {
  status: 'OK' | 'NG'
  userGroupList: FullResp
  results: { status: 'OK' | 'NG'; message: string }[]
  message?: string
}

const CACHE_ID_UGL = 'userGroupList'
const INVALIDATE_CACHE_IDS = [CACHE_ID_UGL, PROFILE_CACHE_ID]
const CACHE_TTL = 10000 // 10 secs

export async function getUsersAndGroups() {
  const res = await (fetch.get('userGroupList', {
    cache: { ttl: CACHE_TTL },
    id: CACHE_ID_UGL,
  }) as Promise<FullResp>)
  return res
}

export async function getUsers(): Promise<User[]> {
  const { users } = await (fetch.get('users') as Promise<{ users: User[] }>)
  return users
}

export async function getSubGroups() {
  const { subGroups } = await (fetch.get('subGroups') as Promise<{
    subGroups: SubGroup[]
  }>)
  return subGroups
}

export async function addUsers(users: AddUserPayload[]): Promise<AddUsersResp> {
  const { data } = await fetch.postInvalidate(
    'addUsers',
    INVALIDATE_CACHE_IDS,
    {
      users,
    },
  )
  return data
}

export async function editUser(user: UserPayload): Promise<FullResp> {
  const { data } = await fetch.put('editUser', user, INVALIDATE_CACHE_IDS)
  return data
}

export async function deleteUser(userID: number): Promise<FullResp> {
  const { data } = await fetch.sendDelete(
    'deleteUser',
    { userID },
    INVALIDATE_CACHE_IDS,
  )
  return data
}

export type EditSubGroupUsersPayload = Pick<SubGroup, 'name'> & {
  users: string[]
}
export type NewSubGroupPayload = Pick<SubGroup, 'operationFlowType'> &
  EditSubGroupUsersPayload

export async function addSubGroup(
  subGroup: NewSubGroupPayload,
): Promise<FullResp> {
  const { data } = await fetch.postInvalidate(
    'subGroups',
    INVALIDATE_CACHE_IDS,
    subGroup,
  )
  return data
}

export async function editSubGroup(
  subGroup: EditSubGroupUsersPayload,
): Promise<FullResp> {
  const { name, users } = subGroup
  const { data } = await fetch.put(
    `subGroups/${encodeURIComponent(name)}`,
    {
      users,
    },
    INVALIDATE_CACHE_IDS,
  )

  return data
}

export async function deleteSubGroup(name: string): Promise<FullResp> {
  const { data } = await fetch.sendDelete(
    `subGroups/${encodeURIComponent(name)}`,
    undefined,
    INVALIDATE_CACHE_IDS,
  )
  return data
}
